import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_PAGE = { visible: false, enabled: false, menu: false, redirect_to: '/' };
const DEFAULT_SECTION = { visible: false };
const DEFAULT_FEATURE = { visible: true, enabled: true };

const FALLBACK = { pages: {}, sections: {}, features: {} };

let cached = null;
let inflight = null;
// Passe à true dès que le fetch initial des content-controls est résolu
// (succès OU échec). Permet de distinguer « pas encore chargé » de « chargé »
// pour éviter que les gardes de route ne décident sur l'état initial FALLBACK.
let loaded = false;
const subscribers = new Set();

async function fetchContentControls() {
  try {
    const res = await axios.get(`${API_URL}/api/settings/content-controls`, { timeout: 5000 });
    const data = res.data;
    cached = data && typeof data === 'object'
      ? {
          pages: (data.pages && typeof data.pages === 'object') ? data.pages : {},
          sections: (data.sections && typeof data.sections === 'object') ? data.sections : {},
          features: (data.features && typeof data.features === 'object') ? data.features : {},
        }
      : FALLBACK;
  } catch {
    cached = FALLBACK;
  }
  loaded = true;
  return cached;
}

export function invalidateContentControlsCache(nextData) {
  if (nextData && typeof nextData === 'object') {
    cached = {
      pages: (nextData.pages && typeof nextData.pages === 'object') ? nextData.pages : {},
      sections: (nextData.sections && typeof nextData.sections === 'object') ? nextData.sections : {},
      features: (nextData.features && typeof nextData.features === 'object') ? nextData.features : {},
    };
    inflight = null;
    loaded = true;
    subscribers.forEach((fn) => { try { fn(cached); } catch { /* no-op */ } });
    return cached;
  }
  cached = null;
  inflight = null;
  const refresh = fetchContentControls();
  refresh.then((data) => {
    subscribers.forEach((fn) => { try { fn(data); } catch { /* no-op */ } });
  });
  return refresh;
}

export function useContentControls() {
  const [cc, setCc] = useState(cached || FALLBACK);
  useEffect(() => {
    let active = true;
    const notify = (data) => { if (active) setCc(data); };
    subscribers.add(notify);
    if (cached) {
      setCc(cached);
    } else {
      if (!inflight) inflight = fetchContentControls();
      inflight.then((data) => { if (active) setCc(data); });
    }
    return () => {
      active = false;
      subscribers.delete(notify);
    };
  }, []);
  return cc;
}

// Indique si les content-controls ont été résolus au moins une fois (fetch
// terminé, succès ou échec). Les gardes de route s'en servent pour ATTENDRE
// les vrais flags au lieu de décider sur l'état initial FALLBACK — sinon race
// au chargement dur : redirection déclenchée avant l'arrivée des flags.
export function useContentControlsLoaded() {
  const [ready, setReady] = useState(loaded);
  useEffect(() => {
    let active = true;
    if (loaded) {
      setReady(true);
      return () => { active = false; };
    }
    if (!inflight) inflight = fetchContentControls();
    inflight.then(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, []);
  return ready;
}

function _pageCfg(cc, key) {
  return { ...DEFAULT_PAGE, ...(((cc && cc.pages) || {})[key] || {}) };
}
function _sectionCfg(cc, key) {
  return { ...DEFAULT_SECTION, ...(((cc && cc.sections) || {})[key] || {}) };
}
function _featureCfg(cc, key) {
  return { ...DEFAULT_FEATURE, ...(((cc && cc.features) || {})[key] || {}) };
}

export function isPageEnabled(cc, key) {
  const p = _pageCfg(cc, key);
  if (p.visible === false) return false;
  return p.enabled === true;
}

export function isPageInMenu(cc, key) {
  const p = _pageCfg(cc, key);
  if (p.visible === false) return false;
  return p.menu === true;
}

export function getPageRedirect(cc, key) {
  return _pageCfg(cc, key).redirect_to || '/';
}

export function isSectionVisible(cc, key) {
  return _sectionCfg(cc, key).visible === true;
}

export function isFeatureVisible(cc, key) {
  return _featureCfg(cc, key).visible !== false;
}

// Lit un drapeau arbitraire d'une feature (ex: auth.login_visible,
// subscriptions.pricing_visible). Fallback true : un drapeau absent reste actif.
export function isFeatureFlag(cc, key, flag) {
  return _featureCfg(cc, key)[flag] !== false;
}

export function isFeatureEnabled(cc, key) {
  return _featureCfg(cc, key).enabled !== false;
}

export default useContentControls;
